import { isBlockedEmailDomain } from '@mnd-apps/public/lib/emailDomainBlockList';
import type { HubspotFormConfiguration } from '@mnd-apps/public/lib/getHubspotForm';
import { ReCaptchaProvider, useReCaptcha } from '@mnd-apps/public/lib/next-recaptcha-v3';
import { track } from '@mnd-apps/public/lib/tracker';
import { getCookieByName } from '@mnd-apps/public/utils/cookies';
import logger from '@mnd-apps/public/utils/logger';
import type { PageFragment } from '@mnd-apps/public/wp-generated/types';
import CardQuotation from '@mnd-frontend/ui/public/components/CardQuotation';
import Form from '@mnd-frontend/ui/public/components/Form';
import Heading from '@mnd-frontend/ui/public/components/typography/Heading';
import Wysiwyg from '@mnd-frontend/ui/public/components/typography/Wysiwyg';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import type { TextColorDataType } from '@mnd-frontend/ui/public/types/color';
import { getTextColor } from '@mnd-frontend/ui/public/utils/maps/color';
import {
  BREAKPOINTS,
  SPACING_2,
  SPACING_3,
  SPACING_5,
  SPACING_10,
  WEB_PURPLE_REG,
  WEB_WHITE_REG,
} from '@mnd-frontend/ui/shared/tokens';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import isEmail from 'nice-is-email';
import { type FormEventHandler, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useToasts } from '../../../../../../libs/ui/publish/src/contexts';
import { isBackgroundDark } from '../../getModuleColors';

const NAVBAR_HEIGHT = '5.5rem';

const Section = styled.section`
  position: relative;
  padding: clamp(5rem, 10vw, 10rem) ${SPACING_2};
  min-height: calc(100vh - ${NAVBAR_HEIGHT});
`;

const BackgroundImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -2;
`;

const Content = styled(ContentMaxWidth)`
  display: flex;
  flex-direction: column-reverse;
  gap: ${SPACING_5};

  @media (min-width: ${BREAKPOINTS.LG}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${SPACING_10};
  }
`;

const DescriptionContainer = styled.div<{ $darkBackground: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_3};
  color: ${({ $darkBackground }) => ($darkBackground ? WEB_WHITE_REG : WEB_PURPLE_REG)};
`;

const QuoteContainer = styled.div`
  display: block;
`;

type FormError = {
  errorMessage: string;
  errorCode: string;
};

type Props = {
  data: NonNullable<PageFragment['hero']>['heroForm'];
  hubspotFormConfig: HubspotFormConfiguration;
  formTrackingName: string;
};

const HeroForm = ({ data, hubspotFormConfig, formTrackingName }: Props) => {
  const [fieldErrors, setFieldErrors] = useState<{
    [fieldName: string]: string;
  }>({});
  const { t } = useTranslation();
  const { executeRecaptcha } = useReCaptcha();
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionCompleted, setSubmissionCompleted] = useState(false);
  const { showSuccessToast } = useToasts();
  const darkBackground = isBackgroundDark(data?.bgColor);

  // In HubSpot forms there are cases where a hidden field is added with a default value
  // for analytics/data grouping purposes. They should just be included in the submission as is.
  const extraFields = useMemo(
    () =>
      hubspotFormConfig.fields
        .filter(field => field.hidden && field.defaultValue)
        .map(field => ({ name: field.name, value: field.defaultValue! })),
    [hubspotFormConfig],
  );

  const setFormError = useCallback((name: string, error?: string) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      if (error) {
        next[name] = error;
      } else {
        delete next[name];
      }
      return next;
    });
  }, []);

  // We memoize this separately so we can rerun all validations on submission
  const formValidationList = useMemo(
    () =>
      Object.fromEntries(
        hubspotFormConfig.fields
          .filter(field => !field.hidden || (field.hidden && field.richText))
          .map(field => [
            field.name,
            // Default validation; a value is given for required fields.
            {
              [field.type]: (value: string) => {
                const valid = (field.required && value.trim().length > 0) || !field.required;
                if (valid) {
                  setFormError(field.name);
                } else {
                  setFormError(field.name, t('form.validation.required'));
                }
                return valid;
              },
              email: (value: string) => {
                const validEmail = isEmail(value);
                const allowedDomain = validEmail && !isBlockedEmailDomain(value);
                const valid = validEmail && allowedDomain;
                if (!validEmail) {
                  setFormError(field.name, t('form.validation.email.format'));
                } else if (!allowedDomain) {
                  setFormError(
                    field.name,
                    t('form.validation.email.blocked', {
                      domain: value.split('@')[1],
                    }),
                  );
                } else {
                  setFormError(field.name);
                }
                return valid;
              },
            }[field.type],
          ]),
      ),
    [t, hubspotFormConfig, setFormError],
  );

  const formFields = useMemo(
    () =>
      hubspotFormConfig.fields
        .filter(field => !field.hidden || (field.hidden && field.richText))
        .map(field => ({
          ...field,
          type: field.hidden
            ? 'richText'
            : {
                [field.type]: field.type,
                single_line_text: 'text',
                single_checkbox: 'checkbox',
                dropdown: 'select',
              }[field.type],
          validate: formValidationList[field.name],
        })),
    [formValidationList, hubspotFormConfig],
  );

  // If the form has any utm_ tracking fields we want to grab them
  // from the path and include in the submission.
  const searchParams = useSearchParams();
  const utmFields = useMemo(
    () =>
      hubspotFormConfig.fields
        .filter(field => field.hidden && field.name.startsWith('utm_'))
        .map(field => ({
          name: field.name,
          value: searchParams.get(field.name) ?? '',
        })),

    [searchParams, hubspotFormConfig.fields.filter],
  );

  const trackError = (error_code: string, error_message: string, form_field: string) => {
    track({
      event: 'error_form_submit',
      traits: {
        event_info: {
          form_id: hubspotFormConfig.hubspotFormId,
          error_code,
          error_message,
          form_field,
          form_name: hubspotFormConfig.name,
          navigation_selection: hubspotFormConfig.submitButtonText,
          user_interaction: 'cta_click',
        },
      },
    });
  };

  const validateForm = useCallback(
    async (formData: FormData) => {
      return [...formData.entries()]
        .map(([name, value]) => formValidationList[name](String(value)))
        .every(Boolean);
    },
    [formValidationList],
  );

  const handleRecpatchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      logger.error('ReCAPTCHA not available');
      return;
    }
    const token = await executeRecaptcha('HeroForm');
    return token;
  }, [executeRecaptcha]);

  const handleOnSubmit: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const validForm = await validateForm(formData);
    if (!validForm) return;

    setSubmissionLoading(true);
    const token = await handleRecpatchaVerify();
    const hubspotCookie = getCookieByName('hubspotutk');

    // Checkboxes in HubSpot expect Yes/No.
    formFields
      .filter(field => field.type === 'checkbox')
      .forEach(field => {
        const checked = formData.get(field.name) === 'on';
        formData.set(field.name, checked ? 'Yes' : 'No');
      });

    try {
      const response = await fetch('/api/form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          hubspotFormId: hubspotFormConfig.hubspotFormId,
          hubspotCookie: hubspotCookie,
          pageName: document.title,
          fields: [...formData.entries()]
            .map(field => ({ name: field[0], value: field[1].toString() }))
            .concat(utmFields)
            .concat(extraFields),
        }),
      });

      setSubmissionLoading(false);

      if (response.ok) {
        track({
          event: 'lead_generation',
          traits: {
            event_info: {
              form_id: hubspotFormConfig.hubspotFormId,
              lead_source: '',
              navigation_selection: hubspotFormConfig.submitButtonText,
              form_name: formTrackingName,
              user_interaction: 'lead_generation',
            },
          },
        });
        setSubmissionCompleted(true);
        if (hubspotFormConfig.redirectUri) {
          window.location.assign(hubspotFormConfig.redirectUri);
        }
        showSuccessToast(hubspotFormConfig.successfulSubmissionText ?? t('common_thank_you'));
      } else if (response.headers.get('content-type')?.includes('application/json')) {
        const { errors: responseErrors }: { errors: Record<string, FormError[]> } =
          await response.json();
        for (const [field, errors] of Object.entries(responseErrors)) {
          for (const { errorCode, errorMessage } of errors) {
            trackError(errorCode, errorMessage, field);
          }
        }
        setFieldErrors(
          Object.fromEntries(
            Object.entries(responseErrors).map(([field, errors]) => [
              field,
              errors.map(error => error.errorMessage).join(', '),
            ]),
          ),
        );
      } else {
        trackError('unknown', 'Failed to submit form', 'unknown');
      }
    } catch (e) {
      setSubmissionLoading(false);
      logger.error({ error: e as Error }, 'Something went wrong');
    }
  };

  const textColor = getTextColor(data?.textColor?.at(0) as TextColorDataType);

  const quote = data?.quotes?.[0];
  const quotationCard = quote && (
    <CardQuotation
      quote={quote?.quote}
      name={quote?.name}
      jobTitle={quote?.jobTitle}
      profileImage={quote?.image}
      colorVariant={textColor === '#FFF' ? 'pink' : 'purple'}
    />
  );

  return (
    <Section>
      <BackgroundImage
        src={data?.image?.node?.mediaItemUrl ?? ''}
        alt={data?.image?.node?.altText ?? ''}
        width={data?.image?.node.mediaDetails?.width ?? 1920}
        height={data?.image?.node.mediaDetails?.height ?? 1080}
        priority
      />
      <Content>
        <DescriptionContainer $darkBackground={darkBackground}>
          <Heading as="h1" color={textColor}>
            {data?.title}
          </Heading>
          <Wysiwyg color="inherit" htmlContent={data?.description} />
          {quote && <QuoteContainer>{quotationCard}</QuoteContainer>}
        </DescriptionContainer>
        <Form
          title={data?.form?.title ?? undefined}
          submitButtonText={hubspotFormConfig.submitButtonText}
          fields={formFields}
          errors={fieldErrors}
          onSubmit={handleOnSubmit}
          loading={submissionLoading}
          disabled={submissionCompleted}
        />
      </Content>
    </Section>
  );
};

const HeroFormWrapper = (props: Props) => {
  return (
    <ReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
      async={false}
      defer={false}
      className="optanon-category-C0001"
    >
      <HeroForm {...props} />
    </ReCaptchaProvider>
  );
};

export default HeroFormWrapper;
